import { useState } from "react";
import { CheckCircle2, Loader2, Send, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { trackEvent, getConsent } from "@/lib/analytics";
import { utmSuffix, getUtm } from "@/lib/utm";

const BUSINESS_TYPES = [
  "Real estate agency",
  "Property management company",
  "Conveyancing firm",
  "Corporate or property services",
  "Other",
];

const INTERESTS = [
  "Client referrals",
  "Property portfolio inspections",
  "Corporate collaboration",
  "Other partnership",
];

const EMPTY = {
  company_name: "", contact_person: "", email: "", phone: "",
  company_website: "", business_type: "", partnership_interest: "", message: "",
  privacy_consent: false,
  website: "", // honeypot — humans never see or fill this field
};

/**
 * Partnership enquiry form (/partners). Posts to /api/partner-enquiries —
 * stored distinctly as "Partnership Enquiry", fully separate from inspection
 * quotation and booking workflows.
 */
export default function PartnerForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e)); };
  const field = "w-full bg-white border border-line rounded-lg px-3.5 py-2.5 text-base sm:text-sm text-ink focus:border-blue focus:ring-2 focus:ring-blue/15 outline-none transition";

  const validate = () => {
    const errs = {};
    if (form.company_name.trim().length < 2) errs.company_name = "Please enter the company name.";
    if (form.contact_person.trim().length < 2) errs.contact_person = "Please enter a contact person.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please enter a valid business email.";
    if (!form.phone.trim()) errs.phone = "Please enter a phone / WhatsApp number.";
    if (!form.business_type) errs.business_type = "Please choose the business type.";
    if (!form.partnership_interest) errs.partnership_interest = "Please choose the partnership interest.";
    if (!form.privacy_consent) errs.privacy_consent = "Please accept the privacy policy to continue.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || done) return; // duplicate-submission guard
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.querySelector("[data-invalid='true']")?.focus();
      return;
    }
    setBusy(true);
    setSubmitError("");
    try {
      await api.post("/partner-enquiries", {
        ...form,
        attribution: { ...getUtm(), consent_status: getConsent() || "unknown" },
        source: `${window.location.pathname}${window.location.search || utmSuffix()}`,
      });
      setDone(true);
      trackEvent("partner_enquiry_submit", { business_type: form.business_type, interest: form.partnership_interest });
      trackEvent("generate_lead", { service: "Partnership enquiry", source_path: window.location.pathname });
    } catch (err) {
      trackEvent("partner_enquiry_error");
      setSubmitError(formatApiError(err?.response?.data?.detail) || "Could not send your enquiry. Your details are kept — please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="partner-form-success">
        <div className="w-16 h-16 rounded-full bg-blue-50 text-blue grid place-items-center mx-auto mb-5"><CheckCircle2 size={32} /></div>
        <h3 className="text-2xl mb-2">Thank you for your interest in partnering with Fedari.</h3>
        <p className="text-muted max-w-[44ch] mx-auto">Our team will review your enquiry and contact you.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="partner-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <L label="Company name *" error={errors.company_name}>
          <input className={field} data-invalid={!!errors.company_name || undefined} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} autoComplete="organization" data-testid="partner-company" />
        </L>
        <L label="Contact person *" error={errors.contact_person}>
          <input className={field} data-invalid={!!errors.contact_person || undefined} value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} autoComplete="name" data-testid="partner-contact-person" />
        </L>
        <L label="Business email *" error={errors.email}>
          <input type="email" className={field} data-invalid={!!errors.email || undefined} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="partner-email" />
        </L>
        <L label="Phone / WhatsApp *" error={errors.phone}>
          <input type="tel" className={field} data-invalid={!!errors.phone || undefined} value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" placeholder="e.g. +971 50 000 0000" data-testid="partner-phone" />
        </L>
        <L label="Company website (optional)">
          <input type="url" className={field} value={form.company_website} onChange={(e) => set("company_website", e.target.value)} placeholder="https://…" data-testid="partner-website" />
        </L>
        <L label="Business type *" error={errors.business_type}>
          <select className={field} data-invalid={!!errors.business_type || undefined} value={form.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="partner-business-type">
            <option value="" disabled>Select business type…</option>
            {BUSINESS_TYPES.map((o) => <option key={o}>{o}</option>)}
          </select>
        </L>
        <L label="Partnership interest *" error={errors.partnership_interest}>
          <select className={field} data-invalid={!!errors.partnership_interest || undefined} value={form.partnership_interest} onChange={(e) => set("partnership_interest", e.target.value)} data-testid="partner-interest">
            <option value="" disabled>Select interest…</option>
            {INTERESTS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </L>
      </div>
      <L label="Message (optional)">
        <textarea rows={3} className={field} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us how you would like to collaborate" data-testid="partner-message" />
      </L>

      {/* Honeypot — visually hidden, bots only */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 pointer-events-none h-0 w-0" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="partner-honeypot" />

      <div>
        <label className="flex items-start gap-2.5 text-sm text-bodytext py-1">
          <input type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} data-invalid={!!errors.privacy_consent || undefined} className="h-5 w-5 mt-0.5 accent-blue" data-testid="partner-privacy-consent" />
          <span>I agree that Fedari may process these details to respond to my partnership enquiry, as described in the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue font-semibold underline">Privacy Policy</a>. *</span>
        </label>
        {errors.privacy_consent && <p className="text-xs text-sev-high mt-1">{errors.privacy_consent}</p>}
      </div>

      {submitError && (
        <div className="flex items-start gap-2.5 bg-[#FDECEC] border border-[#F8C6C9] text-[#C81E2B] rounded-lg px-4 py-3 text-sm" role="alert" data-testid="partner-form-error">
          <AlertTriangle size={17} className="flex-none mt-0.5" />
          <span>{submitError}</span>
        </div>
      )}

      <button type="submit" disabled={busy} data-testid="partner-submit"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue text-white px-8 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]">
        {busy ? <><Loader2 size={17} className="animate-spin" /> Sending…</> : <><Send size={16} /> Request a Partnership</>}
      </button>
    </form>
  );
}

function L({ label, error, children }) {
  return (
    <div>
      <span className="block text-sm font-medium text-ink mb-1.5">{label}</span>
      {children}
      {error && <p className="text-xs text-sev-high mt-1">{error}</p>}
    </div>
  );
}
